import { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  CustomButton,
  CustomTextInput,
  CustomSingleSelect,
} from "@ippon/shared-components";
import type {
  Control,
  FieldErrors,
  FieldValues,
  UseFormRegister,
} from "react-hook-form";
import { resolveSearchControls } from "./searchControls";

/** 表示件数セレクトの選択肢 */
export interface PerPageOption {
  value: number;
  label: string;
}

/** 物件一覧・専有部・共用部・修繕一覧で共通利用するツールバー用 props。検索用と表示件数用で別フォームを扱うためジェネリクスは2つ */
export interface CustomListToolbarProps<
  TSearchForm extends FieldValues = FieldValues,
  TPerPageForm extends FieldValues = FieldValues,
> {
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchRegister?: UseFormRegister<TSearchForm>;
  searchErrors?: FieldErrors<TSearchForm>;
  searchName?: string;
  onSearch?: () => void;
  /** クリアボタン: キーワードと絞り込みをすべて初期状態に戻す。 */
  onClear?: () => void;
  /** キーワード欄に今入っている文字（watch の値）。文字があるときだけ × を出す。 */
  searchValue?: string;
  /** キーワード欄の ×: キーワードだけを消し、適用中のキーワードも外す。未指定なら × を出さない。 */
  onSearchValueClear?: () => void;
  /** キーワード以外の絞り込み（プルダウン等）が画面にあるか。キーワードだけの画面は false（クリアボタンを置かない）。 */
  hasFilterControls?: boolean;
  /**
   * キーワード以外の絞り込みが選ばれているか。true のときだけクリアボタンを出す。
   * false の間も幅は確保し、キーワード欄の幅を変えない。未指定は従来どおり常に出す。
   */
  hasActiveFilters?: boolean;
  /** 検索の右隣に出す要素（修繕一覧のカテゴリなど） */
  leftExtra?: ReactNode;
  perPageOptions: PerPageOption[];
  perPageControl: Control<TPerPageForm>;
  perPageErrors?: FieldErrors<TPerPageForm>;
  perPageName?: string;
  onPerPageChange: (value: number) => void;
  /** 主ボタンのラベル。showPrimaryButton が true の場合に必須です。 */
  primaryButtonLabel?: string;
  /** 主ボタンのクリックハンドラ。showPrimaryButton が true の場合に必須です。 */
  primaryButtonOnClick?: () => void;
  primaryButtonStartIcon?: ReactNode;
  /** 主ボタン（例: 物件新規追加）を表示するか。見積書一覧では false */
  showPrimaryButton?: boolean;
  deleteDisabled?: boolean;
  /** 削除ボタンのクリックハンドラ。showDelete が true の場合に必須です。 */
  onDeleteClick?: () => void;
  showDelete?: boolean;
  /** 縦並び→横並びの切り替えブレイクポイント。未指定は sm(600px)、修繕一覧は md(900px) を指定 */
  responsiveBreakpoint?: "sm" | "md";
  /** true のとき 600-900px ではクリアアイコンを非表示（物件一覧など） */
  clearIconHideBetween600And900?: boolean;
  /** true のとき表示件数セレクトを非表示にする */
  hidePerPage?: boolean;
  /** true のときキーワード検索なしでも検索/クリアボタンを表示する (leftExtra のセレクトで絞り込む用) */
  showFilterActions?: boolean;
}

const primaryButtonSx = {
  height: 36,
  borderRadius: "10px",
  px: 2,
  backgroundColor: "#0b9dbd",
  color: "#ffffff",
  fontFamily: '"Noto Sans JP"',
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "16.8px",
  "&:hover": { backgroundColor: "#0a8aa7" },
};

export function CustomListToolbar<
  TSearchForm extends FieldValues = FieldValues,
  TPerPageForm extends FieldValues = FieldValues,
>({
  showSearch = true,
  searchPlaceholder = "検索ワードを入力",
  searchRegister,
  searchErrors = {},
  searchName = "keyword",
  onSearch,
  onClear,
  searchValue,
  onSearchValueClear,
  hasFilterControls,
  hasActiveFilters,
  leftExtra,
  perPageOptions,
  perPageControl,
  perPageErrors = {},
  perPageName = "perPage",
  onPerPageChange,
  primaryButtonLabel,
  primaryButtonOnClick,
  primaryButtonStartIcon = <AddIcon sx={{ fontSize: 20 }} />,
  showPrimaryButton = true,
  deleteDisabled = false,
  onDeleteClick,
  showDelete = true,
  responsiveBreakpoint = "sm",
  clearIconHideBetween600And900 = false,
  hidePerPage = false,
  showFilterActions = false,
}: CustomListToolbarProps<TSearchForm, TPerPageForm>) {
  const bp = responsiveBreakpoint;
  const showActions = (showSearch && !!searchRegister) || showFilterActions;
  const { showKeywordClear, clearButton } = resolveSearchControls({
    keyword: searchValue,
    hasFilterControls,
    hasActiveFilters,
  });
  return (
    <Stack
      direction={{ xs: "column", [bp]: "row" }}
      spacing={1.25}
      // spacing(margin実装)は flexWrap の段間に効かないため CSS gap を使う。
      // 狭幅で「検索行」と「表示件数/ボタン行」が折り返した時の行間 0 を防ぐ。
      useFlexGap
      mt={1}
      alignItems={{ xs: "stretch", [bp]: "center" }}
      justifyContent="space-between"
      sx={{
        flexWrap: "wrap",
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        "& .MuiButton-root": { display: "flex" },
      }}
    >
      {/* 左ブロック: 狭い幅で縦積み、ブレイクポイント以上で横並び */}
      <Stack
        direction={{ xs: "column", [bp]: "row" }}
        spacing={1.25}
        alignItems={{ xs: "stretch", [bp]: "center" }}
        sx={{
          flex: { xs: "0 0 auto", [bp]: "1 1 400px" },
          width: { xs: "100%", [bp]: "auto" },
          maxWidth: { xs: "100%", [bp]: "560px" },
          minWidth: 0,
          minHeight: 0,
          flexWrap: { [bp]: "nowrap" },
          overflow: "hidden",
          alignSelf: { xs: "stretch", [bp]: "center" },
        }}
      >
        {showSearch && searchRegister && (
          <Box sx={{ width: { xs: "100%", [bp]: "auto" }, minWidth: { [bp]: 100 }, flex: { [bp]: 1 } }}>
            <CustomTextInput
              label=""
              name={searchName}
              placeholder={searchPlaceholder}
              register={searchRegister}
              errors={searchErrors}
              inputWidth="100%"
              inputWidthSp="100%"
              showSearchIconStart
              search={true}
              showClearButton={showKeywordClear && !!onSearchValueClear}
              onClearClick={onSearchValueClear}
              clearButtonAriaLabel="キーワードをクリア"
              sx={{ mb: "0px!important" }}
            />
          </Box>
        )}
        {leftExtra && (
          <Box sx={{ width: { xs: "100%", [bp]: "auto" }, flexShrink: { [bp]: 0 } }}>
            {leftExtra}
          </Box>
        )}
        {showActions && (
          <Stack
            direction="row"
            spacing={1.25}
            sx={{
              width: { xs: "100%", [bp]: "auto" },
              "& .MuiButton-root": { flex: { xs: 1, [bp]: "none" } },
            }}
          >
            <CustomButton
              label="検索"
              buttonCategory="search"
              onClick={onSearch}
            />
            {clearButton !== "none" && (
              <CustomButton
                label="クリア"
                buttonCategory="clear"
                onClick={onClear}
                useMdIcon={true}
                clearIconHideBetween600And900={clearIconHideBetween600And900}
                // 隠す間も場所を残し、出し入れでキーワード欄の幅を変えない。
                // visibility: hidden は押せず、フォーカス・読み上げの対象にもならない。
                sx={{
                  visibility: clearButton === "reserved" ? "hidden" : "visible",
                }}
              />
            )}
          </Stack>
        )}
      </Stack>

      {/* 右ブロック: 表示件数 + 主ボタン + 削除（SPでも横並び） */}
      <Stack
        direction="row"
        spacing={1.25}
        useFlexGap
        justifyContent={{ xs: "flex-start", [bp]: "flex-end" }}
        alignItems="center"
        sx={{
          width: { xs: "100%", [bp]: "auto" },
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" gap={1} alignItems="center" flexShrink={0}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              fontWeight: 700,
              fontSize: "14px",
              color: "#3e3e3e",
              display: hidePerPage ? { xs: "none", sm: "block" } : "block",
            }}
          >
            表示件数
          </Typography>
          <Box sx={{ minWidth: 70 }}>
            <CustomSingleSelect
              label=""
              name={perPageName}
              placeholder=""
              control={perPageControl}
              errors={perPageErrors}
              inputWidth="80px"
              inputWidthSp="100%"
              options={perPageOptions}
              hasSpacing={false}
              onChangeValue={(v) => onPerPageChange(Number(v))}
            />
          </Box>
        </Stack>
        {showPrimaryButton && primaryButtonLabel != null && primaryButtonOnClick != null && (
          <Box sx={{ flex: 1, minWidth: 0, display: "flex" }}>
            <CustomButton
              label={primaryButtonLabel}
              startIcon={primaryButtonStartIcon}
              sx={{ ...primaryButtonSx, width: "100%", justifyContent: "center" }}
              onClick={primaryButtonOnClick}
            />
          </Box>
        )}
        {showDelete && (
          <CustomButton
            label="削除"
            buttonCategory="delete"
            disabled={deleteDisabled}
            onClick={onDeleteClick ?? (() => {})}
            spIcon={true}
          />
        )}
      </Stack>
    </Stack>
  );
}
